import { format, formatDistanceToNowStrict, parseISO } from 'date-fns';
import type { SignalTone } from '../../theme/tokens';
import { type SyncRunKind } from '../../utils/syncRunKind';

/** Mirrors `src/types/storeErpSync.ts`. */
export type StoreSyncStatus = 'running' | 'failing' | 'missed' | 'paused' | 'no_details' | 'never' | 'healthy';
export type StoreSyncTile = 'attention' | 'warnings' | 'healthy' | 'paused' | 'no_details' | 'never';
export type StoreSyncRunResult = 'clean' | 'warnings' | 'failed';
export type StoreSyncFrequency = 'manual' | 'hourly' | 'daily' | 'weekly';

export interface StoreSyncRunPoint {
  at: string;
  result: StoreSyncRunResult;
}

export interface StoreSyncRow {
  integrationId: string;
  platform: string;
  name: string;
  domain: string | null;
  storefrontUrl: string | null;
  organizationId: string | null;
  organizationName: string | null;
  orgIssue: 'none' | 'missing' | null;
  status: StoreSyncStatus;
  tile: StoreSyncTile | null;
  failStreak: number;
  overdueDays: number;
  schedule: { frequency: StoreSyncFrequency; paused: boolean };
  nextRunAt: string | null;
  lastRun: {
    at: string;
    durationMs: number | null;
    kind: SyncRunKind;
    updated: number | null;
    errors: number;
    warnings: number;
  } | null;
  lastSuccessAt: string | null;
  lastSyncAt: string | null;
  strip: StoreSyncRunPoint[];
  runBlockedReason: string | null;
  runKindBlocked: Record<'pricing' | 'compare' | 'full', string | null>;
}

export interface StoreSyncSummary {
  generatedAt: string;
  linked: number;
  unlinked: number;
  tiles: Record<StoreSyncTile, number> & { failing: number; missed: number };
  fleet: {
    days: Array<{ date: string; clean: number; warnings: number; failed: number }>;
    runs: number;
    withoutErrorsPct: number | null;
  };
  /** The server's run read hit its cap: history may be short, and `chart` days may be low. */
  truncated?: { runs: boolean; chart: boolean };
}

export interface StoreSyncErrorDetail {
  code: string;
  title: string;
  fix: string;
  raw: string;
  count: number;
}

export interface StoreSyncWarningGroup {
  code: string;
  title: string;
  count: number;
  samples: string[];
}

export interface StoreSyncRunHistoryRow {
  id: string;
  at: string | null;
  kind: SyncRunKind;
  updated: number | null;
  durationMs: number | null;
  result: StoreSyncRunResult | 'running' | 'cancelled';
}

export interface StoreSyncDetail {
  row: StoreSyncRow;
  errors: StoreSyncErrorDetail[];
  warnings: StoreSyncWarningGroup[];
  runs: StoreSyncRunHistoryRow[];
  hasUnsummarizedRuns: boolean;
  /** The latest admin-started Lightspeed run, run in the background (MOT-524); null when none. */
  adminRun?: AdminStoreSyncRun | null;
}

/** Server `AdminStoreSyncRunView`: a dead job (silent past its window) already reads as `failed`. */
export interface AdminStoreSyncRun {
  runId: string;
  status: 'queued' | 'running' | 'done' | 'failed';
  kind: 'pricing' | 'compare' | 'full';
  requestedAt: string;
  startedAt: string | null;
  finishedAt: string | null;
  syncRunId: string | null;
  runStatus: string | null;
  continues: boolean;
  alreadyRunning: boolean;
  productsProcessed: number | null;
  errorCount: number | null;
  error: string | null;
}

export interface StatusPresentation {
  label: string;
  tone: SignalTone;
  help: string;
}

const STATUS: Record<StoreSyncStatus, StatusPresentation> = {
  running: { label: 'Running', tone: 'info', help: 'A sync is running now.' },
  failing: { label: 'Failing', tone: 'problem', help: 'The last run finished with errors.' },
  missed: {
    label: 'Missed run',
    tone: 'caution',
    help: 'The schedule says a run was due and none started. Check the scheduler or run it now.',
  },
  paused: { label: 'Paused', tone: 'neutral', help: 'The schedule is off. Manual runs still work.' },
  no_details: {
    label: 'No run details',
    tone: 'neutral',
    help: 'This sync finished before Motzu recorded run summaries. Run a sync to get counts and warnings.',
  },
  never: { label: 'Not synced yet', tone: 'neutral', help: 'Connected, but it has never run.' },
  healthy: { label: 'Healthy', tone: 'positive', help: 'The last run finished clean.' },
};

export const presentStatus = (status: StoreSyncStatus): StatusPresentation => STATUS[status];

export const TILE_ORDER: StoreSyncTile[] = ['attention', 'warnings', 'healthy', 'paused', 'no_details', 'never'];

export const TILE_COPY: Record<StoreSyncTile, { label: string; tone: SignalTone }> = {
  attention: { label: 'Needs attention', tone: 'problem' },
  warnings: { label: 'With warnings', tone: 'caution' },
  healthy: { label: 'Healthy', tone: 'positive' },
  paused: { label: 'Paused', tone: 'neutral' },
  no_details: { label: 'No run details', tone: 'neutral' },
  never: { label: 'Not synced yet', tone: 'neutral' },
};

export function tileSubline(tile: StoreSyncTile, summary: StoreSyncSummary): string {
  switch (tile) {
    case 'attention':
      return `${summary.tiles.failing} failing · ${summary.tiles.missed} missed`;
    case 'warnings':
      return 'Synced, but skipped or adjusted items';
    case 'healthy':
      return 'Clean last run';
    case 'paused':
      return 'Schedule off';
    case 'no_details':
      return 'Synced before summaries existed';
    default:
      return 'Connected, never run';
  }
}

export function platformLabel(platform: string): string {
  switch (platform) {
    case 'shopify':
      return 'Shopify';
    case 'bigcommerce':
      return 'BigCommerce';
    case 'lightspeed':
      return 'Lightspeed';
    case 'lightspeed_ecom':
      return 'Lightspeed eCom';
    case 'woocommerce':
      return 'WooCommerce';
    default:
      return platform.charAt(0).toUpperCase() + platform.slice(1);
  }
}

export function statusSubline(row: StoreSyncRow, kindLabel: string): string | null {
  switch (row.status) {
    case 'failing':
      return row.failStreak > 1 ? `${row.failStreak} runs in a row` : 'Last run';
    case 'missed':
      return `Overdue ${row.overdueDays} ${row.overdueDays === 1 ? 'day' : 'days'}`;
    case 'running':
      return kindLabel;
    case 'healthy':
      return row.lastRun?.warnings ? null : 'Clean last run';
    case 'no_details':
      return row.lastSyncAt ? `Synced ${formatDay(row.lastSyncAt)}` : null;
    default:
      return null;
  }
}

export function formatDay(iso: string | null | undefined): string {
  if (!iso) return '—';
  return format(parseISO(iso), 'MMM d');
}

export function formatWhen(iso: string | null | undefined): string {
  if (!iso) return '—';
  return format(parseISO(iso), 'MMM d, HH:mm');
}

export function formatDuration(ms: number | null | undefined): string {
  if (ms === null || ms === undefined) return '';
  const total = Math.round(ms / 1000);
  if (total < 60) return `${total}s`;
  const m = Math.floor(total / 60);
  const s = total % 60;
  return s ? `${m}m ${s}s` : `${m}m`;
}

export function formatAgo(iso: string | null | undefined): string {
  if (!iso) return '—';
  return `${formatDistanceToNowStrict(parseISO(iso))} ago`;
}

/** The Next run cell: a date, or why there is none. */
export function nextRunText(row: StoreSyncRow): { text: string; tone: 'normal' | 'overdue' | 'quiet' } {
  if (row.schedule.paused) return { text: 'Paused', tone: 'quiet' };
  if (row.schedule.frequency === 'manual') return { text: 'Manual', tone: 'quiet' };
  if (row.status === 'missed') {
    return { text: `Overdue ${row.overdueDays} ${row.overdueDays === 1 ? 'day' : 'days'}`, tone: 'overdue' };
  }
  return row.nextRunAt ? { text: formatWhen(row.nextRunAt), tone: 'normal' } : { text: '—', tone: 'quiet' };
}

export function frequencyLabel(frequency: StoreSyncFrequency): string {
  return frequency.charAt(0).toUpperCase() + frequency.slice(1);
}

/** What Copy error puts on the clipboard. */
export function errorClipboardText(errors: StoreSyncErrorDetail[]): string {
  return errors.map((e) => `${e.title}\n${e.raw}`).join('\n\n');
}

const csvCell = (value: string): string => (/[",\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value);

export function runHistoryCsv(runs: StoreSyncRunHistoryRow[], noun: (kind: SyncRunKind) => string): string {
  const header = ['When', 'Type', 'Updated', 'Unit', 'Duration', 'Result'];
  const lines = runs.map((r) =>
    [
      r.at ?? '',
      r.kind,
      r.updated === null ? '' : String(r.updated),
      noun(r.kind),
      formatDuration(r.durationMs),
      r.result,
    ]
      .map(csvCell)
      .join(','),
  );
  return [header.join(','), ...lines].join('\n');
}
